<template>
    <div class="language-switcher">
        <vue-multiselect
            v-model="selectedLanguage"
            :options="availableLanguages"
            :placeholder="$t('common.language')"
            :show-labels="false"
            :searchable="false"
            label="name"
            track-by="name"
            @input="changeLanguage"
        >
            <template
                slot="singleLabel"
                slot-scope="props"
            >
                <div
                    :class="{'text-white': whiteLabel}"
                    class="lang-row flex items-center"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="flag-icon flex-shrink-0"
                        v-html="flags[props.option.key]"
                    />
                    <!-- eslint-enable vue/no-v-html -->
                    <label>{{ props.option.name }}</label>
                </div>
            </template>
            <template #caret="{ toggle }">
                <icon
                    for="arrow-down-sml"
                    :class="{'text-white': whiteLabel}"
                    class="multiselect__caret"
                    @click="toggle"
                />
            </template>
            <template
                slot="option"
                slot-scope="props"
            >
                <div class="lang-row flex">
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="flag-icon"
                        v-html="flags[props.option.key]"
                    />
                    <!-- eslint-enable vue/no-v-html -->
                    <label>{{ props.option.name }}</label>
                </div>
            </template>
        </vue-multiselect>
    </div>
</template>

<script>
import { container } from 'tsyringe';
import { LanguageService } from 'common/services/LanguageService';
import VueMultiselect from 'vue-multiselect/src/Multiselect.vue';
import Icon from 'common/UIToolKit/Icon';
import { appLanguages } from 'static/data/Languages';
import USAFlag from 'ui/svg/usa-flag.svg?raw';
import FinlandFlag from 'ui/svg/finland-flag.svg?raw';
import LatviaFlag from 'ui/svg/latvia-flag.svg?raw';
import LithuaniaFlag from 'ui/svg/lithuania-flag.svg?raw';
import SwedenFlag from 'ui/svg/swedish-flag.svg?raw';
import EstoniaFlag from 'ui/svg/estonia-flag.svg?raw';
import GermanyFlag from 'ui/svg/germany-flag.svg?raw';
import DutchFlag from 'ui/svg/netherlands-flag.svg?raw';
import Permissions from 'common/support/Permissions';

export default {
    components: {
        Icon,
        VueMultiselect,
    },
    props: {
        languages: {
            type: Array,
            default: () => appLanguages,
        },
        whiteLabel: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            languageService: container.resolve(LanguageService),
            permissions: container.resolve(Permissions),
            selectedLanguage: {
                name: 'Suomi',
                key: 'fi-FI',
            },
            flags: {
                'en-GB': USAFlag,
                'fi-FI': FinlandFlag,
                'lv-LV': LatviaFlag,
                'sv-SE': SwedenFlag,
                'et-EE': EstoniaFlag,
                'de-DE': GermanyFlag,
                'lt-LT': LithuaniaFlag,
                'nl-NL': DutchFlag,
            },
        };
    },
    computed: {
        availableLanguages() {
            const languages = this.languages.filter(language => language.key !== this.selectedLanguage.key);
            if (this.permissions.isJobillaUser) {
                return languages;
            }
            return languages.filter(language => language.key !== 'nl-NL');
        },
    },
    created() {
        if (this.$route.name === 'Auth.Login' || this.$route.name === 'Auth.Reset') {
            for (const browserPreferredLanguage of navigator.languages) {
                const matchingAppLanguage = appLanguages.find(appLanguage => appLanguage.alias === browserPreferredLanguage);
                if (matchingAppLanguage) {
                    this.languageService.setLanguage(matchingAppLanguage.key);
                    return this.selectedLanguage = matchingAppLanguage;
                }
            }
        }
        const language = this.languages.find(language => language.key === this.languageService.currentLanguage);
        if (language) {
            return this.selectedLanguage = language;
        }
        return this.selectedLanguage = { key: 'en-GB', name: 'English' };
    },

    methods: {
        changeLanguage(language) {
            if (language.key) {
                this.languageService.setLanguage(language.key);
                this.selectedLanguage = language;
                this.$emit('languageChanged');
            }
        },
    },
};
</script>
<style lang="scss">
@import "common/scss/palette";
@import "common/scss/variables";
@import "common/scss/forms";
@import '@/styles/colors/variables';
@import "common/scss/variables/curves";
@import "common/scss/variables/v2/shadows";

.language-switcher {
    min-height: initial;

    .flag-icon {
        @apply mr-xxs w-6 h-6;
    }

    .lang-row {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;

        &:before {
            width: calc(100% - 8px);
            height: 1px;
            background: $dust;
            content: '';
            position: absolute;
            top: 0;
            left: 4px;
        }
    }

    .multiselect {
        width: 140px;
        min-height: initial;
    }

    .multiselect__caret {
        @apply w-6 h-6 absolute right-0;
        top: 4px;
        transition: linear .15s;
    }

    .multiselect__option--highlight {
        @apply bg-light-gray font-semibold text-midnight;
        cursor: pointer;

        label {
            cursor: pointer;
        }
    }

    .multiselect__option, .multiselect__single {
        @apply text-size-3 leading-3;
        padding: $space-xxxs;
        display: flex;
        justify-content: center;
        min-height: initial;
    }

    .multiselect__single {
        margin: 0;
        background: none;

        &:hover {
            cursor: pointer;

            label {
                cursor: pointer;
            }
        }
    }

    .multiselect__element:first-child, .multiselect__single {
        .lang-row:before {
            display: none;
        }
    }

    .multiselect__tags {
        border: 0;
        padding: 0;
        margin: 0;
        min-height: initial;
    }

    .multiselect__tags {
        border: none;
        background: none;
    }

    .multiselect--active {
        .multiselect__caret {
            transform: rotate(180deg);
        }

        .multiselect__content-wrapper {
            border-radius: 0 0 $border-curve-md $border-curve-md;
            border: 1px solid $dust;
            border-top: none;
            box-shadow: 0 1px map-get($shadows-level-2-map, blur) 0 rgba($charcoal, 0.2);
        }

        &.multiselect--above {
            .multiselect__content-wrapper {
                border-radius: $border-curve-md $border-curve-md 0 0;
                border-bottom: none;
                border-top: 1px solid $dust;
                box-shadow: 0 1px map-get($shadows-level-2-map, blur) 0 rgba($charcoal, 0.2);
            }

            .multiselect__tags {
                border-radius: 0 0 $border-curve-md $border-curve-md;
            }
        }
    }
}

@keyframes mobileNavSliceLeft {
    0% {
        transform: translate(100%, 0%);
    }
    100% {
        transform: translate(0%, 0%);
    }
}
</style>
